import React, { Component } from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../../../utils';
import { withTheme } from 'styled-components';

// Reused Models
import {
    DocumentsGrid as DocumentsGridInterface,
    DocumentActions,
} from '../../../models/configuration/documents/DocumentsGrid';

// Reused Components
import {
    ColumnHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '../../Grid';
import Search from '../../Search';
import PaginationButton, {
    AriaLabelPlaceholders,
} from '../../PaginationButton';
import Translate, { translate } from '../../Translate';
import VisuallyHiddenWrapper from '../../VisuallyHiddenWrapper';
import DocumentsGridActions from '../DocumentsGridActions';

// Utils
import getDocumentEntityQueryResults from '../../../utils/query/getDocumentEntityQueryResults';

// Document Grid Models
import { DocumentColumn, DocumentRow, DocumentsGridProps } from './Models';

// Document Grid Styled Components
import DocumentCard from './StyledComponents/DocumentCard';
import DocumentCardActions from './StyledComponents/DocumentCardActions';
import DocumentCardActionsLabel from './StyledComponents/DocumentCardActionsLabel';
import DocumentRecords from './StyledComponents/DocumentRecords';
import DocumentRecord from './StyledComponents/DocumentRecord';
import DocumentRecordLabel from './StyledComponents/DocumentRecordLabel';
import DocumentRecordValue from './StyledComponents/DocumentRecordValue';
import DocumentsGridWrapper from './StyledComponents/DocumentsGridWrapper';
import DocumentsSearchWrapper from './StyledComponents/DocumentsSearchWrapper';
import DocumentsGridTableCaption from './StyledComponents/DocumentsGridTableCaption';
import DocumentsGridTableWrapper from './StyledComponents/DocumentsGridTableWrapper';
import DocumentsPaginationWrapper from './StyledComponents/DocumentsPaginationWrapper';
import DocumentsPaginationDisplay from './StyledComponents/DocumentsPaginationDisplay';
import DocumentsPaginationButtons from './StyledComponents/DocumentsPaginationButtons';
import { IDocument } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { getDisclaimers } from '../../../utils/disclaimers';
import Disclaimers from '../../../Components/Disclaimers';

@inject('dataStore', 'screenSizeStore', 'translationStore', 'entityStore')
@observer
class DocumentsGrid extends Component<DocumentsGridProps> {
    @observable documentSearch = {
        ...this.documentsGrid.search,
        properties: [], // search component requires a value
        value: '',
    };

    @observable currentPage = 0;

    constructor(props) {
        super(props);
        if (isNullOrEmpty(props.documents)) {
            this.getColumnDocuments();
        }
    }

    getColumnDocuments = async () => {
        const { entityStore, columnDocumentsSearch } = this.props;
        entityStore.getColumnDocumentGridDocuments(
            this.clientCodeColumnId,
            columnDocumentsSearch
        );
    };

    @computed
    get clientCodeColumnId(): string {
        const { entityStore, columnId } = this.props;

        return `${entityStore?.value.clientCode}-${columnId}`;
    }

    @computed
    get searchTheme(): any {
        const { theme } = this.props;
        return {
            ...theme,
            SearchWrapper: theme.DocumentsSearch,
            SearchLabel: theme.DocumentsSearchLabel,
            SearchIcon: theme.DocumentsSearchIcon,
            SearchBadge: theme.DocumentsSearchBadge,
            SearchInput: theme.DocumentsSearchInput,
            SearchCloseIcon: theme.DocumentsSearchCloseIcon,
        };
    }

    @computed
    get documentsGrid(): DocumentsGridInterface {
        const { config } = this.props;
        return config;
    }

    @computed
    get uiOptions(): DocumentsGridInterface['uiOptions'] {
        const { uiOptions = {} } = this.documentsGrid;

        return {
            table: {
                hideTableHead: false,
            },
            dropdown: {
                scrollIntoView: true,
            },
            ...uiOptions,
        };
    }

    @computed
    get caption(): React.ReactNode {
        const { theme, documentType } = this.props;
        const { caption } = this.documentsGrid;
        const { text = documentType, show = false } = caption;

        if (show) {
            return (
                <DocumentsGridTableCaption {...theme.DocumentsGridTableCaption}>
                    <Translate>{text}</Translate>
                </DocumentsGridTableCaption>
            );
        }

        return (
            <VisuallyHiddenWrapper as="caption">
                <Translate>{text}</Translate>
            </VisuallyHiddenWrapper>
        );
    }

    @computed
    get hasSearch(): boolean {
        return Boolean(this.documentsGrid.search);
    }

    @computed
    get hasPagination(): boolean {
        return Boolean(this.documentsGrid.pagination);
    }

    @computed
    get pageSize(): number {
        if (this.hasPagination) {
            const { pageSize = 10 } = this.documentsGrid.pagination || {};
            return pageSize;
        }
        return 0;
    }

    @computed
    get startActions(): DocumentActions {
        const { actions = {} } = this.documentsGrid;
        const { start } = actions;
        return start;
    }

    @computed
    get startActionsHasLabel(): boolean {
        const uiOptions = this.startActions?.uiOptions;
        return uiOptions?.showLabel === true && !!uiOptions?.label;
    }

    @computed
    get startActionsLabel(): string {
        const { translationStore } = this.props;
        const uiOptions = this.startActions?.uiOptions;
        return (
            translationStore &&
            this.startActionsHasLabel &&
            translate(translationStore, uiOptions?.label)
        );
    }

    @computed
    get endActions(): DocumentActions {
        const { actions = {} } = this.documentsGrid;
        const { end } = actions;
        return end;
    }

    @computed
    get endActionsHasLabel(): boolean {
        const uiOptions = this.endActions?.uiOptions;
        return uiOptions?.showLabel === true && !!uiOptions?.label;
    }

    @computed
    get endActionsLabel(): string {
        const { translationStore } = this.props;
        const uiOptions = this.endActions?.uiOptions;
        return (
            translationStore &&
            this.endActionsHasLabel &&
            translate(translationStore, uiOptions?.label)
        );
    }

    @computed
    get columns(): Array<DocumentColumn> {
        const { columns = [] } = this.documentsGrid;

        return columns.map(column => {
            const { label, width, verticalAlign, textAlign, wrapText } = column;
            return {
                ...column,
                key: label,
                styleProps: {
                    width,
                    verticalAlign,
                    textAlign,
                    ...(!wrapText && {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }),
                },
            };
        });
    }

    @computed
    get documents(): Array<DocumentRow> {
        const { documents, dataStore, entityStore } = this.props;
        const { culture } = dataStore;
        const columnDocuments = entityStore.columnDocumentsGridDocuments.get(
            this.clientCodeColumnId
        );

        const workingDocs =
            isNullOrEmpty(documents) && columnDocuments
                ? columnDocuments
                : documents;
        return workingDocs.map(document => ({
            document,
            columns: this.columns.map(column => {
                const { key, query, styleProps } = column;
                const value = getDocumentEntityQueryResults(
                    query,
                    (document as unknown) as IDocument,
                    documents,
                    culture,
                    dataStore,
                    entityStore?.value as any
                );

                return {
                    key,
                    value,
                    styleProps,
                };
            }),
        }));
    }

    @computed
    get filteredDocuments(): Array<DocumentRow> {
        return this.documents.filter(document =>
            document.columns.some(column =>
                column.value
                    ?.toLowerCase()
                    .includes(this.documentSearch.value.toLowerCase())
            )
        );
    }

    @computed
    get pagedDocuments(): Array<DocumentRow> {
        if (this.hasPagination) {
            return this.filteredDocuments.slice(
                this.previousPage,
                this.nextPage
            );
        }

        return this.filteredDocuments;
    }

    @computed
    get total(): number {
        return this.filteredDocuments.length;
    }

    @computed
    get totalPages(): number {
        return Math.ceil(this.filteredDocuments.length / this.pageSize);
    }

    @computed
    get nextPage(): number {
        return Math.min(this.pageSize * (this.currentPage + 1), this.total);
    }

    @computed
    get previousPage(): number {
        return Math.max(this.pageSize * this.currentPage, 0);
    }

    @computed
    get previousPageDisabled(): boolean {
        return this.currentPage === 0;
    }

    @computed
    get nextPageDisabled(): boolean {
        return this.currentPage === this.totalPages - 1;
    }

    @computed
    get prevPageAriaLabelPlaceholders(): AriaLabelPlaceholders {
        const prev = this.previousPageDisabled
            ? this.previousPage + 1
            : this.previousPage + 1 - this.pageSize;
        const next = this.previousPageDisabled
            ? this.nextPage
            : this.previousPage;

        return {
            prev,
            next,
            total: this.total,
        };
    }

    @computed
    get nextPageAriaLabelPlaceholders(): AriaLabelPlaceholders {
        const prev = this.nextPageDisabled
            ? this.previousPage + 1
            : this.nextPage + 1;
        const next = Math.min(
            this.pageSize * (this.currentPage + 2),
            this.total
        );

        return {
            prev,
            next,
            total: this.total,
        };
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        const { dataStore, entityStore } = this.props;
        const { noDataDisclaimerTypes } = this.documentsGrid;
        // If the noDataDisclaimerTypes are configured then use it
        if (noDataDisclaimerTypes) {
            const { disclaimers = {} } = entityStore.value;
            const noDataDisclaimers = getDisclaimers(
                dataStore,
                noDataDisclaimerTypes,
                disclaimers,
                entityStore.queryContext
            );
            return noDataDisclaimers;
        }
        // Else use the noDocumentsDisclaimers on the entityStore as a fallback
        return entityStore.noDocumentsDisclaimers;
    }

    @action
    resetCurrentPage = () => {
        this.currentPage = 0;
    };

    @action
    search = (searchTerm: string) => {
        this.documentSearch.value = searchTerm;
        this.resetCurrentPage();
    };

    @action
    clearSearch = () => {
        this.documentSearch.value = '';
        this.resetCurrentPage();
    };

    @action
    prev = () => {
        this.currentPage = this.currentPage - 1 < 0 ? 0 : this.currentPage - 1;
    };

    @action
    next = () => {
        this.currentPage =
            this.currentPage + 1 >= this.totalPages
                ? this.currentPage
                : this.currentPage + 1;
    };

    getStyleProps(actions: DocumentActions) {
        const { uiOptions: column } = actions;

        if (column) {
            const { width, verticalAlign, textAlign, wrapText } = column;

            return {
                width,
                verticalAlign,
                textAlign,
                ...(!wrapText && {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                }),
            };
        }

        return {};
    }

    render() {
        const { theme, dataStore, screenSizeStore } = this.props;
        const metricIds = {
            search: 'documentSearch',
            closeIcon: 'documentSearch-close',
        };
        const showDocumentCards = screenSizeStore.compareBreakpoints(
            dataStore.breakpointSize,
            '<=',
            'md'
        );

        const noDataDisclaimers = this.noDataDisclaimers;
        const disclaimersTheme = {
            ...theme,
            DisclaimersList: theme.DocumentsGridDisclaimersList,
            DisclaimerListItem: theme.DocumentsGridDisclaimerListItem,
            DisclaimerCharacter: theme.DocumentsGridDisclaimerCharacter,
            DisclaimerContent: theme.DocumentsGridDisclaimerContent,
            DisclaimerReturnLink: theme.DocumentsGridDisclaimerReturnLink,
        };

        return (
            <DocumentsGridWrapper {...theme.DocumentsGridWrapper}>
                {this.hasSearch && (
                    <DocumentsSearchWrapper {...theme.DocumentsSearchWrapper}>
                        <Search
                            total={this.total}
                            search={this.documentSearch}
                            accessibilityStoreKey="documentSearch"
                            searchInputId="documentSearch"
                            onChange={this.search}
                            clearSearch={this.clearSearch}
                            metricIds={metricIds}
                            hasFocus={true}
                            scrollIntoView={
                                this.uiOptions.dropdown.scrollIntoView
                            }
                            theme={this.searchTheme}
                        />
                    </DocumentsSearchWrapper>
                )}
                <DocumentsGridTableWrapper {...theme.DocumentsGridTableWrapper}>
                    {showDocumentCards ? (
                        this.pagedDocuments.length === 0 ? (
                            <Disclaimers
                                disclaimers={noDataDisclaimers}
                                theme={disclaimersTheme}
                            />
                        ) : (
                            this.pagedDocuments.map(({ document, columns }) => {
                                return (
                                    <DocumentCard
                                        key={`doc-card-${document.clientCode}`}
                                        {...theme.GridDocumentCard}
                                    >
                                        {this.startActions && (
                                            <DocumentCardActions
                                                {...theme.GridDocumentCardActions}
                                            >
                                                <DocumentCardActionsLabel
                                                    {...theme.GridDocumentCardActionsLabel}
                                                >
                                                    {this.startActionsLabel}
                                                </DocumentCardActionsLabel>
                                                <DocumentsGridActions
                                                    {...this.props}
                                                    document={document}
                                                    actions={this.startActions}
                                                />
                                            </DocumentCardActions>
                                        )}
                                        <DocumentRecords
                                            {...theme.GridDocumentRecords}
                                        >
                                            {columns.map(column => {
                                                return (
                                                    <DocumentRecord
                                                        key={`doc-card-record-${column.key}`}
                                                        {...theme.GridDocumentRecord}
                                                    >
                                                        <DocumentRecordLabel
                                                            {...theme.GridDocumentRecordLabel}
                                                        >
                                                            {column.key}
                                                        </DocumentRecordLabel>
                                                        <DocumentRecordValue
                                                            {...theme.GridDocumentRecordValue}
                                                        >
                                                            {column.value}
                                                        </DocumentRecordValue>
                                                    </DocumentRecord>
                                                );
                                            })}
                                        </DocumentRecords>
                                        {this.endActions && (
                                            <DocumentCardActions
                                                {...theme.GridDocumentCardActions}
                                            >
                                                <DocumentCardActionsLabel
                                                    {...theme.GridDocumentCardActionsLabel}
                                                >
                                                    {this.endActionsLabel}
                                                </DocumentCardActionsLabel>
                                                <DocumentsGridActions
                                                    {...this.props}
                                                    document={document}
                                                    actions={this.endActions}
                                                />
                                            </DocumentCardActions>
                                        )}
                                    </DocumentCard>
                                );
                            })
                        )
                    ) : (
                        <Table {...theme.DocumentsGridTable}>
                            {this.caption}
                            {!this.uiOptions.table.hideTableHead && (
                                <TableHead
                                    position="sticky"
                                    top="0"
                                    bgColor="white"
                                    {...theme.DocumentsGridTableHead}
                                >
                                    <TableRow
                                        {...theme.DocumentsGridTableHeadRow}
                                    >
                                        {this.startActions && (
                                            <ColumnHeader
                                                padding="sm"
                                                scope="col"
                                                as={
                                                    this.startActionsHasLabel
                                                        ? 'th'
                                                        : 'td'
                                                }
                                                {...theme.DocumentsGridTableHeadCell}
                                                {...this.getStyleProps(
                                                    this.startActions
                                                )}
                                            >
                                                {this.startActionsLabel}
                                            </ColumnHeader>
                                        )}
                                        {this.columns.map(column => (
                                            <ColumnHeader
                                                key={column.label}
                                                padding="sm"
                                                scope="col"
                                                as={column.label ? 'th' : 'td'}
                                                {...theme.DocumentsGridTableHeadCell}
                                                {...column.styleProps}
                                            >
                                                <Translate>
                                                    {column.label}
                                                </Translate>
                                            </ColumnHeader>
                                        ))}
                                        {this.endActions && (
                                            <ColumnHeader
                                                padding="sm"
                                                scope="col"
                                                as={
                                                    this.endActionsHasLabel
                                                        ? 'th'
                                                        : 'td'
                                                }
                                                {...theme.DocumentsGridTableHeadCell}
                                                {...this.getStyleProps(
                                                    this.endActions
                                                )}
                                            >
                                                {this.endActionsLabel}
                                            </ColumnHeader>
                                        )}
                                    </TableRow>
                                </TableHead>
                            )}
                            <TableBody {...theme.DocumentsGridTableBody}>
                                {this.pagedDocuments.length === 0 ? (
                                    <Disclaimers
                                        disclaimers={noDataDisclaimers}
                                        theme={disclaimersTheme}
                                    />
                                ) : (
                                    this.pagedDocuments.map(
                                        ({ document, columns }) => (
                                            <TableRow
                                                key={document.clientCode}
                                                {...theme.DocumentsGridTableRow}
                                            >
                                                {this.startActions && (
                                                    <TableCell
                                                        id={document.clientCode}
                                                        padding="sm"
                                                        verticalAlign="top"
                                                        {...theme.DocumentsGridTableCell}
                                                        {...this.getStyleProps(
                                                            this.startActions
                                                        )}
                                                    >
                                                        <DocumentsGridActions
                                                            {...this.props}
                                                            document={document}
                                                            actions={
                                                                this
                                                                    .startActions
                                                            }
                                                        />
                                                    </TableCell>
                                                )}
                                                {columns.map(column => (
                                                    <TableCell
                                                        key={column.key}
                                                        padding="sm"
                                                        verticalAlign="top"
                                                        {...theme.DocumentsGridTableCell}
                                                        {...column.styleProps}
                                                    >
                                                        <Translate>
                                                            {column.value}
                                                        </Translate>
                                                    </TableCell>
                                                ))}
                                                {this.endActions && (
                                                    <TableCell
                                                        id={document.clientCode}
                                                        padding="sm"
                                                        verticalAlign="top"
                                                        {...theme.DocumentsGridTableCell}
                                                        {...this.getStyleProps(
                                                            this.endActions
                                                        )}
                                                    >
                                                        <DocumentsGridActions
                                                            {...this.props}
                                                            document={document}
                                                            actions={
                                                                this.endActions
                                                            }
                                                        />
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        </Table>
                    )}
                </DocumentsGridTableWrapper>
                {this.hasPagination && (
                    <DocumentsPaginationWrapper
                        {...theme.DocumentsPaginationWrapper}
                    >
                        <DocumentsPaginationDisplay
                            {...theme.DocumentsPaginationDisplay}
                        >
                            {this.previousPage + 1} - {this.nextPage} /{' '}
                            {this.total}
                        </DocumentsPaginationDisplay>
                        <DocumentsPaginationButtons
                            {...theme.DocumentsPaginationButtons}
                        >
                            <PaginationButton
                                label="Prev"
                                onClick={this.prev}
                                showLabel={true}
                                iconFileName="PaginationButtonPrevious"
                                iconAlignment="left"
                                buttonAccessibilityValueKey="DocumentsPaginationButtonPrevious"
                                iconAccessibilityValueKey="DocumentsPaginationButtonIconPrevious"
                                ariaLabelPlaceholders={
                                    this.prevPageAriaLabelPlaceholders
                                }
                                disabled={this.previousPageDisabled}
                                {...theme.DocumentsPaginationButtonPrevious}
                            />
                            <PaginationButton
                                label="Next"
                                onClick={this.next}
                                showLabel={true}
                                iconFileName="PaginationButtonNext"
                                buttonAccessibilityValueKey="DocumentsPaginationButtonNext"
                                iconAccessibilityValueKey="DocumentsPaginationButtonIconNext"
                                ariaLabelPlaceholders={
                                    this.nextPageAriaLabelPlaceholders
                                }
                                disabled={this.nextPageDisabled}
                                {...theme.DocumentsPaginationButtonNext}
                            />
                        </DocumentsPaginationButtons>
                    </DocumentsPaginationWrapper>
                )}
            </DocumentsGridWrapper>
        );
    }
}

export default withTheme(DocumentsGrid) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DocumentsGrid/index.tsx